// API Client with CSRF token support
const API_BASE_URL = process.env.REACT_APP_API_URL  || 'http://localhost:8000';


// CSRF Token management
let csrfToken = null;

const getCsrfToken = async () => {
  if (csrfToken) return csrfToken;
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/frontend/auth/csrf-token/`, {
      method: 'GET',
      credentials: 'include',
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.success && data.data.csrfToken) {
        csrfToken = data.data.csrfToken;
        return csrfToken;
      }
    }
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
  }
  
  return null;
};

const clearCsrfToken = () => {
  csrfToken = null;
};

// API Client class
class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Default options
    const defaultOptions = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    // Merge options
    const requestOptions = { ...defaultOptions, ...options };

    // Add CSRF token for POST, PUT, PATCH, DELETE requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(requestOptions.method?.toUpperCase())) {
      const token = await getCsrfToken();
      if (token) {
        requestOptions.headers['X-CSRFToken'] = token;
      }
    }

    try {
      const response = await fetch(url, requestOptions);
      
      // Handle CSRF token refresh on 403 errors
      if (response.status === 403) {
        const data = await response.json();
        if (data.detail && data.detail.includes('CSRF')) {
          // Clear token and retry once
          clearCsrfToken();
          const newToken = await getCsrfToken();
          if (newToken) {
            requestOptions.headers['X-CSRFToken'] = newToken;
            return await fetch(url, requestOptions);
          }
        }
      }

      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  // POST request
  async post(endpoint, data = null, options = {}) {
    const requestOptions = { ...options, method: 'POST' };
    if (data) {
      requestOptions.body = JSON.stringify(data);
    }
    return this.request(endpoint, requestOptions);
  }

  // PUT request
  async put(endpoint, data = null, options = {}) {
    const requestOptions = { ...options, method: 'PUT' };
    if (data) {
      requestOptions.body = JSON.stringify(data);
    }
    return this.request(endpoint, requestOptions);
  }

  // PATCH request
  async patch(endpoint, data = null, options = {}) {
    const requestOptions = { ...options, method: 'PATCH' };
    if (data) {
      requestOptions.body = JSON.stringify(data);
    }
    return this.request(endpoint, requestOptions);
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create and export a singleton instance
const apiClient = new ApiClient();

export default apiClient;
export { getCsrfToken, clearCsrfToken };